<script setup lang="ts">
import { inject, onBeforeUnmount, ref, watch } from 'vue'
import Sortable from 'sortablejs'
import TodoRow from './TodoRow.vue'
import type { TodoNode } from '@/todos/tree'
import { TODO_TREE_CONTEXT } from '@/todos/treeState'

defineOptions({ name: 'TodoTree' })

const props = defineProps<{
  nodes: TodoNode[]
  parentUid: string | null
  sortable: boolean
  depth: number
}>()
const context = inject(TODO_TREE_CONTEXT)!

const list = ref<HTMLElement | null>(null)
let instance: Sortable | null = null

// Drag-to-reorder only within one level (siblings under the same parent), only by the handle --
// so a plain tap or scroll never starts a drag on touch screens.
watch(
  [list, () => props.sortable],
  ([element, enabled]) => {
    instance?.destroy()
    instance = null
    if (!element || !enabled) return
    instance = Sortable.create(element, {
      handle: '.drag-handle',
      draggable: '>.tree-item',
      animation: 150,
      delay: 0,
      onEnd: (event) => {
        const uid = (event.item.querySelector('.todo-row') as HTMLElement | null)?.dataset.uid
        if (!uid || event.oldIndex === event.newIndex || event.newIndex === undefined) return
        // Let Vue own the DOM order again: undo Sortable's move, the store re-renders it.
        const { from, item, oldIndex } = event
        from.removeChild(item)
        from.insertBefore(item, from.children[oldIndex ?? 0] ?? null)
        context.reorder(props.parentUid, uid, event.newIndex)
      },
    })
  },
  { immediate: true },
)

onBeforeUnmount(() => instance?.destroy())
</script>

<template>
  <ul ref="list" class="todo-tree" :class="{ nested: depth > 0 }">
    <li v-for="node in nodes" :key="node.todo.uid" class="tree-item">
      <TodoRow :node="node" :draggable="sortable" />
      <TodoTree
        v-if="node.children.length && !context.collapsed.value.has(node.todo.uid)"
        :nodes="node.children"
        :parent-uid="node.todo.uid"
        :sortable="sortable"
        :depth="depth + 1"
      />
    </li>
  </ul>
</template>

<style scoped>
.todo-tree {
  margin: 0;
  padding: 0;
  list-style: none;
}

.todo-tree.nested {
  margin-left: 1.4rem;
  border-left: 2px solid var(--tg-secondary-bg-color);
}

.tree-item + .tree-item {
  border-top: 1px solid var(--tg-secondary-bg-color);
}

.nested > .tree-item {
  border-top: none;
}

:global(.sortable-ghost) {
  opacity: 0.4;
}
</style>
